import { MAX_GUIDES, type GeoGuide } from './geometry'
/**
 * Colour grade settings (Huanrui, 2026-10-15)
 * -------------------------------------------------------------
 * What a Colour grade card keeps: every slider of the studio
 * (GradeStudio.tsx), the tone curves, the colour mixer, the grading wheels,
 * detail, effects and the gradient and radial masks. Plain numbers only, so
 * it is saved with the board as it is, and copied with the card.
 *
 * Ranges follow Camera Raw: most sliders run -100..100, exposure in stops
 * (-5..5). The engine (gradeEngine.ts) turns them into shader values.
 */

/** A point on a tone curve: input and output, both 0..1. */
export type CurvePoint = [number, number]

export interface ToneCurves {
  rgb: CurvePoint[]
  r: CurvePoint[]
  g: CurvePoint[]
  b: CurvePoint[]
}

/** One grading wheel: a hue (degrees), how much of it (0..100), and lighter or darker (-100..100). */
export interface GradeWheel {
  hue: number
  sat: number
  lum: number
}

/** What a mask may change, where it reaches. The same units as the global sliders. */
export interface LocalAdjust {
  exposure: number
  contrast: number
  highlights: number
  shadows: number
  whites: number
  blacks: number
  temperature: number
  tint: number
  saturation: number
  clarity: number
  dehaze: number
  texture: number
}

export type MaskKind = 'linear' | 'radial'

/**
 * A gradient or radial mask. Places are fractions of the picture's width
 * and height, so they hold at any size.
 *  - linear: full effect at (x1, y1), none at (x2, y2), a smooth fade between.
 *  - radial: an ellipse round (cx, cy), radii rx (of the width) and ry (of
 *    the height), turned by `angle` degrees; `feather` 0..100 softens its
 *    edge; `invert` changes the outside instead.
 */
export interface GradeMask {
  id: string
  kind: MaskKind
  name: string
  hidden?: boolean
  x1: number
  y1: number
  x2: number
  y2: number
  cx: number
  cy: number
  rx: number
  ry: number
  angle: number
  feather: number
  invert: boolean
  adjust: LocalAdjust
}

export interface GradeSettings {
  v: 1
  /* Basic */
  temperature: number
  tint: number
  exposure: number
  contrast: number
  highlights: number
  shadows: number
  whites: number
  blacks: number
  texture: number
  clarity: number
  dehaze: number
  vibrance: number
  saturation: number
  /* Curve */
  curves: ToneCurves
  /* Colour mixer: red, orange, yellow, green, aqua, blue, purple, magenta */
  mixHue: number[]
  mixSat: number[]
  mixLum: number[]
  /* Colour grading */
  gradeShadows: GradeWheel
  gradeMidtones: GradeWheel
  gradeHighlights: GradeWheel
  gradeGlobal: GradeWheel
  gradeBlending: number
  gradeBalance: number
  /* Detail */
  sharpen: number
  sharpenRadius: number
  sharpenMasking: number
  noise: number
  colorNoise: number
  /* Effects */
  vignette: number
  vignetteMidpoint: number
  vignetteFeather: number
  grain: number
  grainSize: number
  /* Masks */
  masks: GradeMask[]
  /* Geometry (geometry.ts, 2026-10-30): applied first, before everything above. */
  geoVertical: number
  geoHorizontal: number
  geoRotate: number
  geoAspect: number
  geoScale: number
  geoOffsetX: number
  geoOffsetY: number
  /** Guided: the lines drawn, in shares of the picture before geometry. They set the sliders. */
  geoGuides: GeoGuide[]
}

export const MIXER_BANDS = ['Red', 'Orange', 'Yellow', 'Green', 'Aqua', 'Blue', 'Purple', 'Magenta'] as const
/** The hue each band is centred on, in degrees. */
export const MIXER_CENTRES = [0, 30, 60, 120, 180, 240, 270, 300]
/** How each band is drawn in the studio. */
export const MIXER_SWATCHES = ['#e8483b', '#f08a2c', '#e9cf3a', '#48b85a', '#3cc4c9', '#3b6ee8', '#8b4fe0', '#d94cc0']

const ZERO8 = () => [0, 0, 0, 0, 0, 0, 0, 0]
const STRAIGHT = (): CurvePoint[] => [
  [0, 0],
  [1, 1],
]
const wheel = (): GradeWheel => ({ hue: 0, sat: 0, lum: 0 })

export function emptyLocal(): LocalAdjust {
  return {
    exposure: 0,
    contrast: 0,
    highlights: 0,
    shadows: 0,
    whites: 0,
    blacks: 0,
    temperature: 0,
    tint: 0,
    saturation: 0,
    clarity: 0,
    dehaze: 0,
    texture: 0,
  }
}

/** A new card's settings: nothing changed. */
export function defaultGrade(): GradeSettings {
  return {
    v: 1,
    temperature: 0,
    tint: 0,
    exposure: 0,
    contrast: 0,
    highlights: 0,
    shadows: 0,
    whites: 0,
    blacks: 0,
    texture: 0,
    clarity: 0,
    dehaze: 0,
    vibrance: 0,
    saturation: 0,
    curves: { rgb: STRAIGHT(), r: STRAIGHT(), g: STRAIGHT(), b: STRAIGHT() },
    mixHue: ZERO8(),
    mixSat: ZERO8(),
    mixLum: ZERO8(),
    gradeShadows: wheel(),
    gradeMidtones: wheel(),
    gradeHighlights: wheel(),
    gradeGlobal: wheel(),
    gradeBlending: 50,
    gradeBalance: 0,
    sharpen: 0,
    sharpenRadius: 1,
    sharpenMasking: 0,
    noise: 0,
    colorNoise: 0,
    vignette: 0,
    vignetteMidpoint: 50,
    vignetteFeather: 50,
    grain: 0,
    grainSize: 25,
    masks: [],
    geoVertical: 0,
    geoHorizontal: 0,
    geoRotate: 0,
    geoAspect: 0,
    geoScale: 100,
    geoOffsetX: 0,
    geoOffsetY: 0,
    geoGuides: [],
  }
}

/**
 * Whatever was saved, made whole: a card saved by an older version, or with
 * a value missing, gets the default for what is missing rather than NaN in
 * the shader.
 */
export function normaliseGrade(raw: unknown): GradeSettings {
  const base = defaultGrade()
  if (!raw || typeof raw !== 'object') return base
  const r = raw as Record<string, unknown>
  const out = { ...base } as unknown as Record<string, unknown>
  for (const key of Object.keys(base) as (keyof GradeSettings)[]) {
    const want = base[key]
    const got = r[key]
    if (typeof want === 'number') out[key] = typeof got === 'number' && Number.isFinite(got) ? got : want
  }
  const curves = (r.curves ?? {}) as Record<string, unknown>
  const curveOf = (one: unknown): CurvePoint[] =>
    Array.isArray(one) && one.length >= 2
      ? one
          .filter((p): p is [number, number] => Array.isArray(p) && typeof p[0] === 'number' && typeof p[1] === 'number')
          .map((p) => [clamp01(p[0]), clamp01(p[1])] as CurvePoint)
          .sort((a, b) => a[0] - b[0])
      : STRAIGHT()
  out.curves = { rgb: curveOf(curves.rgb), r: curveOf(curves.r), g: curveOf(curves.g), b: curveOf(curves.b) }
  const eight = (one: unknown) =>
    Array.isArray(one) && one.length === 8 ? one.map((n) => (typeof n === 'number' && Number.isFinite(n) ? n : 0)) : ZERO8()
  out.mixHue = eight(r.mixHue)
  out.mixSat = eight(r.mixSat)
  out.mixLum = eight(r.mixLum)
  const wheelOf = (one: unknown): GradeWheel => {
    const w = (one ?? {}) as Record<string, unknown>
    const n = (v: unknown) => (typeof v === 'number' && Number.isFinite(v) ? v : 0)
    return { hue: n(w.hue), sat: n(w.sat), lum: n(w.lum) }
  }
  out.gradeShadows = wheelOf(r.gradeShadows)
  out.gradeMidtones = wheelOf(r.gradeMidtones)
  out.gradeHighlights = wheelOf(r.gradeHighlights)
  out.gradeGlobal = wheelOf(r.gradeGlobal)
  out.masks = Array.isArray(r.masks) ? r.masks.map(normaliseMask).filter((m): m is GradeMask => m !== null).slice(0, MAX_MASKS) : []
  out.geoGuides = Array.isArray(r.geoGuides)
    ? (r.geoGuides as Record<string, unknown>[])
        .filter((g) => g && ['x1', 'y1', 'x2', 'y2'].every((k) => typeof g[k] === 'number' && Number.isFinite(g[k])))
        .map((g) => ({ x1: clamp01(g.x1 as number), y1: clamp01(g.y1 as number), x2: clamp01(g.x2 as number), y2: clamp01(g.y2 as number) }))
        .slice(0, MAX_GUIDES)
    : []
  out.v = 1
  return out as unknown as GradeSettings
}

/** The most masks one card holds; the shader has room for this many. */
export const MAX_MASKS = 8

function normaliseMask(raw: unknown): GradeMask | null {
  if (!raw || typeof raw !== 'object') return null
  const r = raw as Record<string, unknown>
  const kind: MaskKind = r.kind === 'radial' ? 'radial' : 'linear'
  const fresh = newMask(kind, 0)
  const n = (key: keyof GradeMask) => (typeof r[key] === 'number' && Number.isFinite(r[key]) ? (r[key] as number) : (fresh[key] as number))
  const adjust = emptyLocal()
  const a = (r.adjust ?? {}) as Record<string, unknown>
  for (const key of Object.keys(adjust) as (keyof LocalAdjust)[]) {
    if (typeof a[key] === 'number' && Number.isFinite(a[key])) adjust[key] = a[key] as number
  }
  return {
    id: typeof r.id === 'string' ? r.id : fresh.id,
    kind,
    name: typeof r.name === 'string' ? r.name : fresh.name,
    hidden: r.hidden === true,
    x1: n('x1'),
    y1: n('y1'),
    x2: n('x2'),
    y2: n('y2'),
    cx: n('cx'),
    cy: n('cy'),
    rx: n('rx'),
    ry: n('ry'),
    angle: n('angle'),
    feather: n('feather'),
    invert: r.invert === true,
    adjust,
  }
}

let maskSeq = 0
/** A new mask where it makes sense to start: a gradient down from the top, or an ellipse in the middle. */
export function newMask(kind: MaskKind, count: number): GradeMask {
  maskSeq += 1
  return {
    id: `mask-${Date.now().toString(36)}-${maskSeq}`,
    kind,
    name: `${kind === 'linear' ? 'Linear' : 'Radial'} ${count + 1}`,
    x1: 0.5,
    y1: 0.05,
    x2: 0.5,
    y2: 0.5,
    cx: 0.5,
    cy: 0.5,
    rx: 0.3,
    ry: 0.3,
    angle: 0,
    feather: 50,
    invert: false,
    adjust: emptyLocal(),
  }
}

export const clamp01 = (n: number) => Math.min(1, Math.max(0, n))

/* ---------------------------------------------------------------- *
 * Tone curves
 * ---------------------------------------------------------------- */

export function isStraight(points: CurvePoint[]): boolean {
  return points.every(([x, y]) => Math.abs(x - y) < 1e-4)
}

/**
 * A smooth curve through the points that never overshoots between them
 * (monotone cubic, Fritsch–Carlson), sampled `size` times over 0..1.
 */
export function sampleCurve(points: CurvePoint[], size: number): Float32Array {
  const out = new Float32Array(size)
  const pts = [...points].sort((a, b) => a[0] - b[0])
  const n = pts.length
  if (n === 0) {
    for (let i = 0; i < size; i += 1) out[i] = i / (size - 1)
    return out
  }
  if (n === 1) {
    out.fill(pts[0][1])
    return out
  }
  const xs = pts.map((p) => p[0])
  const ys = pts.map((p) => p[1])
  const d: number[] = []
  const m: number[] = []
  for (let i = 0; i < n - 1; i += 1) d.push((ys[i + 1] - ys[i]) / Math.max(1e-6, xs[i + 1] - xs[i]))
  m.push(d[0])
  for (let i = 1; i < n - 1; i += 1) m.push(d[i - 1] * d[i] <= 0 ? 0 : (d[i - 1] + d[i]) / 2)
  m.push(d[n - 2])
  for (let i = 0; i < n - 1; i += 1) {
    if (d[i] === 0) {
      m[i] = 0
      m[i + 1] = 0
      continue
    }
    const a = m[i] / d[i]
    const b = m[i + 1] / d[i]
    const s = a * a + b * b
    if (s > 9) {
      const t = 3 / Math.sqrt(s)
      m[i] = t * a * d[i]
      m[i + 1] = t * b * d[i]
    }
  }
  let seg = 0
  for (let i = 0; i < size; i += 1) {
    const x = i / (size - 1)
    if (x <= xs[0]) {
      out[i] = ys[0]
      continue
    }
    if (x >= xs[n - 1]) {
      out[i] = ys[n - 1]
      continue
    }
    while (seg < n - 2 && x > xs[seg + 1]) seg += 1
    const h = xs[seg + 1] - xs[seg]
    const t = (x - xs[seg]) / h
    const t2 = t * t
    const t3 = t2 * t
    out[i] = clamp01(
      (2 * t3 - 3 * t2 + 1) * ys[seg] +
        (t3 - 2 * t2 + t) * h * m[seg] +
        (-2 * t3 + 3 * t2) * ys[seg + 1] +
        (t3 - t2) * h * m[seg + 1],
    )
  }
  return out
}

/* ---------------------------------------------------------------- *
 * What has been changed
 * ---------------------------------------------------------------- */

/** How many things differ from a fresh card: the card says "7 changes". */
export function changesIn(s: GradeSettings): number {
  const base = defaultGrade()
  let count = 0
  for (const key of Object.keys(base) as (keyof GradeSettings)[]) {
    const want = base[key]
    if (typeof want === 'number' && key !== 'v' && Math.abs((s[key] as number) - want) > 1e-6) count += 1
  }
  for (const key of ['rgb', 'r', 'g', 'b'] as const) if (!isStraight(s.curves[key])) count += 1
  for (const list of [s.mixHue, s.mixSat, s.mixLum]) count += list.filter((n) => n !== 0).length
  for (const w of [s.gradeShadows, s.gradeMidtones, s.gradeHighlights, s.gradeGlobal]) if (w.sat !== 0 || w.lum !== 0) count += 1
  count += s.masks.length
  return count
}

/* ---------------------------------------------------------------- *
 * Auto
 * ---------------------------------------------------------------- */

const toLinear = (v: number) => (v <= 0.04045 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4))

/**
 * A starting point from the picture's own brightness, as Camera Raw's Auto
 * gives: the middle brought to a comfortable grey, the ends opened to near
 * black and near white, a touch of vibrance. `luma` is the original's
 * histogram, 256 bins.
 */
export function autoTone(luma: number[]): Partial<GradeSettings> {
  const total = luma.reduce((a, b) => a + b, 0)
  if (total <= 0) return {}
  const at = (share: number) => {
    let run = 0
    for (let i = 0; i < 256; i += 1) {
      run += luma[i]
      if (run >= share * total) return i / 255
    }
    return 1
  }
  const low = at(0.005)
  const mid = at(0.5)
  const high = at(0.995)
  const round = (n: number) => Math.round(n)
  const exposure = Math.max(-1.5, Math.min(1.5, Math.log2(toLinear(0.45) / Math.max(0.002, toLinear(Math.max(0.02, mid))))))
  const spread = high - low
  return {
    exposure: Math.round(exposure * 20) / 20,
    contrast: round(Math.max(0, Math.min(25, (0.85 - spread) * 60))),
    whites: round(Math.max(-30, Math.min(40, (0.97 - high) * 200))),
    blacks: round(Math.max(-40, Math.min(20, -(low - 0.02) * 250))),
    highlights: high > 0.98 ? -25 : 0,
    shadows: mid < 0.3 ? 20 : 0,
    vibrance: 10,
  }
}
